import { Question } from "survey-core";
import { ICalculationResult, VisualizerBase } from "./visualizerBase";
import { localization } from "./localizationManager";
import { VisualizationManager } from "./visualizationManager";
import { mathStatisticsCalculator } from "./statisticCalculators";
import { createDropdown } from "./utils/dropdownWidget";

export interface INumericVisualizerOptions {
  displayValueName: string;
}

export class NumberModel extends VisualizerBase implements INumericVisualizerOptions {
  private _statistics: ICalculationResult;

  public static stepsCount = 5;
  public static generateTextsCallback: (
    question: Question,
    maxValue: number,
    minValue: number,
    stepsCount: number,
    texts: string[]
  ) => string[];

  protected chartTypes: Array<string> = [];
  chartType: String;

  public static showAsPercentage = false;

  public displayValueName = "average";

  private initChartTypes(): void {
    if(VisualizerBase.chartAdapterType) {
      this._chartAdapter = new VisualizerBase.chartAdapterType(this);
      this.chartTypes = this._chartAdapter.getChartTypes();
    }
    if(this.options.availableTypes) {
      this.chartTypes = this.options.availableTypes;
    }

    if(this.chartTypes?.length > 0) {
      [this.questionOptions?.chartType, this.options.defaultChartType].some(type => {
        if(!!type && this.chartTypes.indexOf(type) !== -1) {
          this.chartType = type;
          return true;
        }
        return false;
      });
    }
    if(!this.chartType) {
      const chartTypeCandidate = this.questionOptions?.chartType || this.options.defaultChartType || this.chartTypes[0];
      if(this.chartTypes.indexOf(chartTypeCandidate) !== -1) {
        this.chartType = chartTypeCandidate;
      } else if(this.chartTypes.length > 0) {
        this.chartType = this.chartTypes[0];
      }
    }
  }

  constructor(
    question: Question,
    data: Array<{ [index: string]: any }>,
    options: { [index: string]: any } = {},
    type?: string
  ) {
    super(question, data, options, type || "average");

    if(!!this.question.displayValueName) {
      this.displayValueName = this.question.displayValueName;
    }

    this.initChartTypes();

    if(this.allowChangeType) {
      this.registerToolbarItem("changeChartType", () => {
        if(this.chartTypes.length > 1) {
          return createDropdown({
            options: this.chartTypes.map((chartType) => {
              return {
                value: chartType,
                text: localization.getString("chartType_" + chartType),
              };
            }),
            isSelected: (option: any) => this.chartType === option.value,
            handler: (e: any) => {
              this.setChartType(e);
            }
          });
        }
        return null;
      }, "dropdown");
    }
  }

  protected onDataChanged() {
    this._statistics = undefined;
    super.onDataChanged();
  }

  protected onChartTypeChanged() {}

  protected setChartType(chartType: string) {
    if(
      this.chartTypes.indexOf(chartType) !== -1 &&
      this.chartType !== chartType
    ) {
      this.chartType = chartType;
      this.onChartTypeChanged();
      if(!!this.contentContainer) {
        this.destroyContent(this.contentContainer);
        this.renderContent(this.contentContainer);
      }
      this.invokeOnUpdate();
    }
  }

  destroy(): void {
    this._statistics = undefined;
    super.destroy();
  }

  generateText(maxValue: number, minValue: number, stepsCount: number): string[] {
    let texts: any = [];

    if(stepsCount === 5) {
      texts = [
        "very high (" + maxValue + ")",
        "high",
        "medium",
        "low",
        "very low (" + minValue + ")",
      ];
    } else {
      texts.push(maxValue);
      for(let i = 0; i < stepsCount - 2; i++) {
        texts.push("");
      }
      texts.push(minValue);
    }

    if(!!NumberModel.generateTextsCallback) {
      return NumberModel.generateTextsCallback(
        this.question,
        maxValue,
        minValue,
        stepsCount,
        texts
      );
    }

    return texts;
  }

  generateValues(maxValue: number, stepsCount: number): number[] {
    const values = [];
    for(let i = 0; i < stepsCount; i++) {
      values.push(maxValue / stepsCount);
    }
    values.push(maxValue);
    return values;
  }

  generateColors(maxValue: number, minValue: number, stepsCount: number): string[] {
    const palette = this.getColors();
    const colors = [];
    for(let i = 0; i < stepsCount; i++) {
      colors.push(palette[i]);
    }
    colors.push("rgba(255, 255, 255, 0)");
    return colors;
  }

  protected getCalculatedValuesCore(): ICalculationResult {
    if(this._statistics === undefined) {
      this._statistics = mathStatisticsCalculator(this.surveyData, this);
    }
    return this._statistics;
  }

  public getValues(): Array<any> {
    return this._statistics ? this._statistics.values : [];
  }
}

VisualizationManager.registerVisualizer("number", NumberModel, 200, "average");
VisualizationManager.registerVisualizer("rating", NumberModel, 200, "average");
VisualizationManager.registerVisualizer("expression", NumberModel, undefined, "average");
VisualizationManager.registerVisualizer("gauge", NumberModel, undefined, "average");
VisualizationManager.registerVisualizer("average", NumberModel, undefined, "average");